import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_WATCH_REQUEST_TIMEOUT_MS,
  DEFAULT_WATCH_TOPIC_SCHEME,
  type WatchCommand,
  type WatchConfigTableName,
  type WatchRoom,
} from './watchSocket.constants';
import type {
  BatteryInputReadingFreshness,
  UseWatchSocketOptions,
  UseWatchSocketReturn,
  WatchConfigOptions,
  WatchInboundMessage,
  WatchMessages,
  WatchOutboundMessage,
  WatchPublishAck,
  WatchRoomAck,
  WatchSocketTransport,
} from './watchSocket.types';
import { parseCloudInboundMessage } from './watchSocketCloudMessages';
import { createRequestId, matchesCorrelationKey } from './watchSocketCorrelation';
import {
  accumulateAssetMessages,
  buildChangeConfigMessage,
  buildCommandMessage,
  buildRequestConfigMessage,
  buildRoomTopic,
  isReplyRoom,
  parseInboundMessage,
} from './watchSocketMessages';

interface PendingRequest {
  correlationKey: string;
  resolve: (message: WatchInboundMessage) => void;
  reject: (error: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

const isPublishAck = (value: unknown): value is WatchPublishAck =>
  typeof value === 'object' && value !== null && 'status' in value;

/**
 * Connects to the Socket.IO namespace hmi/local_backend/proxy exposes, joins the room of every
 * requested topic, filters out anything that is not an accepted message or a reply to a request
 * of this instance, and exposes the remaining traffic typed to the consumer.
 */
export const useWatchSocket = ({
  unitId,
  rooms,
  createTransport,
  topicScheme = DEFAULT_WATCH_TOPIC_SCHEME,
  authToken,
  enabled = true,
  requestTimeoutMs = DEFAULT_WATCH_REQUEST_TIMEOUT_MS,
}: UseWatchSocketOptions): UseWatchSocketReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<WatchMessages>({});
  const [joinedTopics, setJoinedTopics] = useState<string[]>([]);
  const [rejectedTopics, setRejectedTopics] = useState<string[]>([]);
  const [batteryInputFreshness, setBatteryInputFreshness] = useState<BatteryInputReadingFreshness>({
    timeseries: false,
    inputData: false,
  });

  const transportRef = useRef<WatchSocketTransport | null>(null);
  const pendingRequestsRef = useRef<PendingRequest[]>([]);
  const isConnectedRef = useRef(false);
  const joinedTopicsRef = useRef<string[]>([]);
  const inputFreshRef = useRef({ timeseries: false, inputData: false });

  // Keyed by content so an inline `rooms`/`topicScheme` literal from the caller does not restart
  // the connection on every render.
  const roomsKey = rooms.join(',');
  const topicSchemeKey = JSON.stringify(topicScheme);
  // A topic is exactly the room name once assembled - kept as pairs so listener setup never has
  // to reverse-parse a topic string back into the room it was built from.
  const roomTopics = useMemo(
    () => rooms.map((room) => ({ room, topic: buildRoomTopic(unitId, room, topicScheme) })),
    [unitId, roomsKey, topicSchemeKey]
  );
  const topics = useMemo(() => roomTopics.map(({ topic }) => topic), [roomTopics]);

  // Payloads describe one unit only, so switching streams must not leave the previous one's data behind.
  const streamKey = `${unitId}|${topicSchemeKey}|${roomsKey}`;
  const streamKeyRef = useRef(streamKey);

  if (streamKeyRef.current !== streamKey) {
    streamKeyRef.current = streamKey;
    inputFreshRef.current = { timeseries: false, inputData: false };
    setMessages({});
    setBatteryInputFreshness({ timeseries: false, inputData: false });
    setJoinedTopics([]);
    setRejectedTopics([]);
  }

  // Mirrored into a ref so `send` reading it mid-event never sees a value from the previous render.
  const applyConnectionState = useCallback((connected: boolean, connecting: boolean) => {
    isConnectedRef.current = connected;
    setIsConnected(connected);
    setIsConnecting(connecting);
  }, []);

  const invalidateBatteryInputReadings = useCallback(() => {
    inputFreshRef.current = { timeseries: false, inputData: false };
    setBatteryInputFreshness({ timeseries: false, inputData: false });
  }, []);

  const markBatteryInputRoom = useCallback((room: WatchRoom) => {
    if (room !== 'timeseries' && room !== 'components/input_data') {
      return;
    }

    const next = {
      timeseries: inputFreshRef.current.timeseries || room === 'timeseries',
      inputData: inputFreshRef.current.inputData || room === 'components/input_data',
    };
    inputFreshRef.current = next;
    setBatteryInputFreshness(next);
  }, []);

  const resolvePendingRequests = useCallback((message: WatchInboundMessage): boolean => {
    const matched = pendingRequestsRef.current.filter((pending) =>
      matchesCorrelationKey(pending.correlationKey, message)
    );

    if (matched.length === 0) {
      return false;
    }

    pendingRequestsRef.current = pendingRequestsRef.current.filter((pending) => !matched.includes(pending));
    matched.forEach((pending) => {
      clearTimeout(pending.timeoutId);
      pending.resolve(message);
    });

    return true;
  }, []);

  const parseMessage = topicScheme.payloadFormat === 'data-points' ? parseCloudInboundMessage : parseInboundMessage;

  const handleTopicMessage = useCallback(
    (room: WatchRoom, rawEnvelope: unknown) => {
      const message = parseMessage(room, rawEnvelope);

      if (!message) {
        return;
      }

      const isReplyToOwnRequest = resolvePendingRequests(message);

      if (isReplyRoom(room) && !isReplyToOwnRequest) {
        return;
      }

      markBatteryInputRoom(room);

      const shouldAccumulateMessages = message.room === 'assets/state' || message.room === 'assets/timeseries';

      setMessages((previous) => {
        if (!shouldAccumulateMessages) {
          return { ...previous, [room]: message } as WatchMessages;
        }

        return {
          ...previous,
          [room]: accumulateAssetMessages({
            previous: previous[room] as Extract<WatchInboundMessage, { room: 'assets/state' | 'assets/timeseries' }>,
            message: message as Extract<WatchInboundMessage, { room: 'assets/state' | 'assets/timeseries' }>,
          }),
        };
      });
    },
    [resolvePendingRequests, parseMessage, markBatteryInputRoom]
  );

  const join = useCallback(
    (transport: WatchSocketTransport) => {
      transport.emit('join', { topics }, (response: unknown) => {
        const ack = response as Partial<WatchRoomAck> | undefined;
        joinedTopicsRef.current = ack?.joined ?? [];
        setJoinedTopics(joinedTopicsRef.current);
        setRejectedTopics(ack?.rejected ?? []);
      });
    },
    [topics]
  );

  const connect = useCallback(() => {
    applyConnectionState(false, true);
    setError(null);

    const transport = createTransport({ authToken });
    transportRef.current = transport;

    const handleConnect = () => {
      invalidateBatteryInputReadings();
      applyConnectionState(true, false);
      setError(null);
      join(transport);
    };

    transport.on('connect', handleConnect);

    transport.on('disconnect', () => {
      invalidateBatteryInputReadings();
      applyConnectionState(false, false);
    });

    transport.on('connect_error', (connectionError: unknown) => {
      invalidateBatteryInputReadings();
      applyConnectionState(false, false);
      setError(connectionError instanceof Error ? connectionError.message : 'Watch socket connection failed');
    });

    roomTopics.forEach(({ room, topic }) => {
      transport.on(topic, (payload: unknown) => handleTopicMessage(room, payload));
    });

    // A transport wrapping an already-open socket will never see another 'connect'.
    if (transport.isConnected?.()) {
      handleConnect();
    }
  }, [
    createTransport,
    authToken,
    roomTopics,
    handleTopicMessage,
    applyConnectionState,
    invalidateBatteryInputReadings,
    join,
  ]);

  const disconnect = useCallback(() => {
    const transport = transportRef.current;

    if (transport && isConnectedRef.current && joinedTopicsRef.current.length > 0) {
      transport.emit('leave', { topics: joinedTopicsRef.current });
    }

    transport?.disconnect();
    transportRef.current = null;
    joinedTopicsRef.current = [];
    invalidateBatteryInputReadings();
    applyConnectionState(false, false);

    // Nothing can answer a request once its transport is gone, so fail fast instead of leaving
    // the consumer's promise hanging until the timeout - or forever, after unmount.
    const pending = pendingRequestsRef.current;
    pendingRequestsRef.current = [];
    pending.forEach((request) => {
      clearTimeout(request.timeoutId);
      request.reject(new Error('Watch socket disconnected before a reply arrived'));
    });
  }, [applyConnectionState, invalidateBatteryInputReadings]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    connect();

    return disconnect;
  }, [enabled, connect, disconnect]);

  const send = useCallback(
    (message: WatchOutboundMessage): Promise<WatchInboundMessage> => {
      const transport = transportRef.current;

      if (!transport || !isConnectedRef.current) {
        return Promise.reject(new Error('Watch socket is not connected'));
      }

      return new Promise<WatchInboundMessage>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          pendingRequestsRef.current = pendingRequestsRef.current.filter((pending) => pending.timeoutId !== timeoutId);
          reject(new Error(`Watch socket request timed out: ${message.correlationKey}`));
        }, requestTimeoutMs);

        pendingRequestsRef.current = [
          ...pendingRequestsRef.current,
          { correlationKey: message.correlationKey, resolve, reject, timeoutId },
        ];

        // The proxy acks a publish immediately with accept/reject - a rejection (unproxied
        // topic, insufficient access level, bad payload) will never be followed by a feedback
        // message, so fail fast instead of waiting out the full timeout.
        transport.emit(message.topic, { payload: message.payload }, (response: unknown) => {
          if (isPublishAck(response) && response.status !== 'ok') {
            pendingRequestsRef.current = pendingRequestsRef.current.filter(
              (pending) => pending.timeoutId !== timeoutId
            );
            clearTimeout(timeoutId);
            reject(new Error(response.error ?? `Watch socket publish to ${response.topic} was ${response.status}`));
          }
        });
      });
    },
    [requestTimeoutMs]
  );

  const sendCommand = useCallback(
    (command: WatchCommand, args?: unknown) =>
      send(buildCommandMessage({ unitId, command, args, topicScheme, requestId: createRequestId() })),
    [send, unitId, topicScheme]
  );

  const requestConfig = useCallback(
    (table: WatchConfigTableName) => send(buildRequestConfigMessage({ unitId, table, topicScheme })),
    [send, unitId, topicScheme]
  );

  const changeConfig = useCallback(
    (table: WatchConfigTableName, options: WatchConfigOptions) =>
      send(buildChangeConfigMessage({ unitId, table, options, topicScheme })),
    [send, unitId, topicScheme]
  );

  return {
    isConnected,
    isConnecting,
    error,
    messages,
    batteryInputReadingsCurrent: batteryInputFreshness.timeseries && batteryInputFreshness.inputData,
    batteryInputFreshness,
    joinedTopics,
    rejectedTopics,
    sendCommand,
    requestConfig,
    changeConfig,
  };
};
